import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await axios.post(`${API_BASE}/auth/forgot-password`, { email });
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-ocean-mesh grid place-items-center p-6">
            <div className="w-full max-w-md bg-white rounded-3xl border border-sand-200 shadow-card p-8">
                <div className="flex items-center justify-between">
                    <Logo />
                    <Link to="/login" className="text-xs text-muted-foreground hover:text-secondary inline-flex items-center gap-1">
                        <ArrowLeft className="h-3 w-3" /> Back to login
                    </Link>
                </div>
                {sent ? (
                    <div className="mt-8 text-center">
                        <div className="h-14 w-14 rounded-full bg-secondary/10 grid place-items-center mx-auto">
                            <Mail className="h-6 w-6 text-secondary" />
                        </div>
                        <h1 className="font-heading text-2xl text-ocean-700 mt-4">Check your inbox</h1>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            If an account exists for <span className="font-medium text-ocean-700">{email}</span>,
                            we just sent a reset link. The link expires in 60 minutes.
                        </p>
                    </div>
                ) : (
                    <>
                        <h1 className="font-heading text-2xl text-ocean-700 mt-6">Reset your password</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Enter your email and we'll send you a link to set a new one.
                        </p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-1.5"
                                    placeholder="you@company.com"
                                    data-testid="forgot-email-input"
                                    autoFocus
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={busy}
                                className="w-full bg-ocean-700 text-white"
                                data-testid="forgot-submit-btn"
                            >
                                {busy ? "Sending..." : "Send reset link"}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
